import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass, createCommentVNode as _createCommentVNode, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, toDisplayString as _toDisplayString, normalizeStyle as _normalizeStyle, createStaticVNode as _createStaticVNode } from "vue"

const _hoisted_1 = { class: "rich-editor-wrapper" }
const _hoisted_2 = {
  key: 0,
  class: "ql-toolbar flex flex-wrap items-center gap-1 p-2 border-b border-gray-200"
}
const _hoisted_3 = ["disabled"]
const _hoisted_4 = ["disabled"]
const _hoisted_5 = ["onClick"]

import { ref, onMounted, onBeforeUnmount, watch } from 'vue';
import { useEditor, EditorContent } from '@tiptap/vue-3';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import Youtube from '@tiptap/extension-youtube';
import TextAlign from '@tiptap/extension-text-align';
import Underline from '@tiptap/extension-underline';
import { useStore } from 'vuex';
import { POST_ACTIVITY_IMAGE } from '@/store/upload.module';
import Swal from 'sweetalert2';


export default /*@__PURE__*/_defineComponent({
  __name: 'RichTextEditor',
  props: {
    modelValue: {}
  },
  emits: ["update:modelValue", "change"],
  setup(__props: any, { emit: __emit }) {

const props = __props;
const emit = __emit;

const store = useStore();

const selectedImageEl = ref<HTMLImageElement | null>(null);
const imageToolbarStyle = ref({});
const currentImageWidth = ref('original');

const CustomImage = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      style: {
        default: null,
        parseHTML: el => el.getAttribute('style'),
        renderHTML: attrs => attrs.style ? { style: attrs.style } : {},
      },
    };
  },
});

const imageSizes = [
  { label: 'S', value: '25%' },
  { label: 'M', value: '50%' },
  { label: 'L', value: '75%' },
  { label: 'XL', value: '90%' },
  { label: 'Orig', value: 'original' },
];

const setImageWidth = (width: string) => {
  if (!editor.value) return;
  currentImageWidth.value = width;
  editor.value.chain().focus().updateAttributes('image', {
    style: width === 'original' ? '' : `width: ${width}; height: auto;`
  }).run();
};

const updateToolbarPos = () => {
  if (!selectedImageEl.value) return;
  const rect = selectedImageEl.value.getBoundingClientRect();
  imageToolbarStyle.value = {
    top: `${rect.top + window.scrollY - 44}px`,
    left: `${rect.left + window.scrollX}px`,
  };
};

const handleImageClick = (e: MouseEvent) => {
  const target = e.target as HTMLElement;
  if (target.tagName === 'IMG') {
    selectedImageEl.value = target as HTMLImageElement;
    currentImageWidth.value = (target as HTMLImageElement).style.width || 'original';
    updateToolbarPos();
  } else if (selectedImageEl.value && !target.closest('.image-toolbar')) {
    selectedImageEl.value = null;
  }
};

const editor = useEditor({
  content: props.modelValue || '',
  extensions: [
    StarterKit,
    Underline,
    CustomImage.configure({
      inline: true,
      allowBase64: false,
      HTMLAttributes: {},
    }),
    Link.configure({ openOnClick: false, HTMLAttributes: { target: '_blank', rel: 'noopener noreferrer' } }),
    Youtube.configure({ width: 800, height: 400 }),
    TextAlign.configure({ types: ['heading', 'paragraph', 'image'] }),
  ],
  editorProps: {
    attributes: {
      class: 'tiptap-content',
    },
  },
  onUpdate: ({ editor }) => {
    emit('update:modelValue', editor.getHTML());
    emit('change');
  },
});

const setLink = () => {
  const prev = editor.value?.getAttributes('link').href || '';
  Swal.fire({
    title: 'Tambah Link',
    input: 'text',
    inputValue: prev,
    inputPlaceholder: 'https://...',
    showCancelButton: true,
    confirmButtonText: 'Set',
    confirmButtonColor: '#2563eb',
    cancelButtonColor: '#6b7280',
    cancelButtonText: 'Batal',
  }).then(result => {
    if (result.isConfirmed) {
      if (!result.value) {
        editor.value?.chain().focus().unsetLink().run();
      } else {
        editor.value?.chain().focus().setLink({ href: result.value }).run();
      }
    }
  });
};

const addImage = () => {
  Swal.fire({
    title: 'Tambah Gambar',
    html: `
      <div style="display:flex;flex-direction:column;gap:12px;text-align:left">
        <div>
          <label style="font-size:13px;color:#6b7280;display:block;margin-bottom:4px">Upload dari komputer</label>
          <input id="swal-img-file" type="file" accept="image/*" style="font-size:13px;width:100%"/>
        </div>
        <div style="text-align:center;color:#9ca3af;font-size:12px">— atau —</div>
        <div>
          <label style="font-size:13px;color:#6b7280;display:block;margin-bottom:4px">URL Gambar</label>
          <input id="swal-img-url" type="text" placeholder="https://..." style="width:100%;padding:6px 10px;border:1px solid #e5e7eb;border-radius:6px;font-size:13px"/>
        </div>
      </div>
    `,
    showCancelButton: true,
    confirmButtonText: 'Tambah',
    confirmButtonColor: '#2563eb',
    cancelButtonColor: '#6b7280',
    cancelButtonText: 'Batal',
    preConfirm: async () => {
      const file = (document.getElementById('swal-img-file') as HTMLInputElement)?.files?.[0];
      const url = (document.getElementById('swal-img-url') as HTMLInputElement)?.value;
      if (file) return await store.dispatch(POST_ACTIVITY_IMAGE, file);
      if (url) {
        try { new URL(url); return url; }
        catch { Swal.showValidationMessage('URL tidak valid'); }
      } else {
        Swal.showValidationMessage('Pilih file atau masukkan URL');
      }
    }
  }).then(result => {
    if (result.isConfirmed && result.value) {
      editor.value?.chain().focus().setImage({ src: result.value }).run();
    }
  });
};

const getYoutubeVideoId = (value: string) => {
  const trimmed = value?.trim();
  if (!trimmed) return null;

  try {
    const hasProtocol = /^https?:\/\//i.test(trimmed);
    const url = new URL(hasProtocol ? trimmed : `https://${trimmed}`);
    const host = url.hostname.toLowerCase().replace(/^www\./, '').replace(/^m\./, '');
    const segments = url.pathname.split('/').filter(Boolean);
    let videoId: string | null = null;

    if (host === 'youtu.be') {
      videoId = segments[0] || null;
    } else if (host === 'youtube.com') {
      if (url.pathname === '/watch') {
        videoId = url.searchParams.get('v');
      } else if (['live', 'embed'].includes(segments[0])) {
        videoId = segments[1] || null;
      }
    }

    return videoId && /^[A-Za-z0-9_-]{11}$/.test(videoId) ? videoId : null;
  } catch {
    return null;
  }
};

const normalizeYoutubeUrl = (value: string) => {
  const videoId = getYoutubeVideoId(value);
  return videoId ? `https://www.youtube.com/watch?v=${videoId}` : null;
};

const addYoutube = () => {
  Swal.fire({
    title: 'Tambah Video YouTube',
    input: 'text',
    inputPlaceholder: 'https://youtube.com/watch?v=... atau https://youtube.com/live/...',
    showCancelButton: true,
    confirmButtonText: 'Tambah',
    confirmButtonColor: '#2563eb',
    cancelButtonColor: '#6b7280',
    cancelButtonText: 'Batal',
    inputValidator: (value) => {
      if (!normalizeYoutubeUrl(value)) return 'URL YouTube tidak valid';
    }
  }).then(result => {
    if (result.isConfirmed) {
      const normalizedUrl = normalizeYoutubeUrl(result.value);
      if (normalizedUrl) {
        editor.value?.chain().focus().setYoutubeVideo({ src: normalizedUrl }).run();
      }
    }
  });
};

watch(() => props.modelValue, (val) => {
  if (!editor.value) return;
  if (editor.value.getHTML() !== val) {
    editor.value.commands.setContent(val || ''); // HAPUS argument false
  }
});

onMounted(() => {
  document.addEventListener('click', handleImageClick);
});

onBeforeUnmount(() => {
  document.removeEventListener('click', handleImageClick);
  editor.value?.destroy();
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (_unref(editor))
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          _createElementVNode("button", {
            onClick: _cache[0] || (_cache[0] = ($event: any) => (_unref(editor).chain().focus().toggleBold().run())),
            class: _normalizeClass([_unref(editor).isActive('bold') ? 'is-active' : '', "toolbar-btn"]),
            title: "Bold"
          }, [...(_cache[16] || (_cache[16] = [
            _createElementVNode("svg", {
              class: "w-4 h-4",
              fill: "currentColor",
              viewBox: "0 0 24 24"
            }, [
              _createElementVNode("path", { d: "M6 4h8a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z" }),
              _createElementVNode("path", { d: "M6 12h9a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z" })
            ], -1)
          ]))], 2),
          _createElementVNode("button", {
            onClick: _cache[1] || (_cache[1] = ($event: any) => (_unref(editor).chain().focus().toggleItalic().run())),
            class: _normalizeClass([_unref(editor).isActive('italic') ? 'is-active' : '', "toolbar-btn"]),
            title: "Italic"
          }, [...(_cache[17] || (_cache[17] = [
            _createElementVNode("svg", {
              class: "w-4 h-4",
              fill: "none",
              stroke: "currentColor",
              viewBox: "0 0 24 24"
            }, [
              _createElementVNode("line", {
                x1: "19",
                y1: "4",
                x2: "10",
                y2: "4"
              }),
              _createElementVNode("line", {
                x1: "14",
                y1: "20",
                x2: "5",
                y2: "20"
              }),
              _createElementVNode("line", {
                x1: "15",
                y1: "4",
                x2: "9",
                y2: "20"
              })
            ], -1)
          ]))], 2),
          _createElementVNode("button", {
            onClick: _cache[2] || (_cache[2] = ($event: any) => (_unref(editor).chain().focus().toggleUnderline().run())),
            class: _normalizeClass([_unref(editor).isActive('underline') ? 'is-active' : '', "toolbar-btn"]),
            title: "Underline"
          }, [...(_cache[18] || (_cache[18] = [
            _createElementVNode("svg", {
              class: "w-4 h-4",
              fill: "none",
              stroke: "currentColor",
              viewBox: "0 0 24 24"
            }, [
              _createElementVNode("path", { d: "M6 3v7a6 6 0 0 0 6 6 6 6 0 0 0 6-6V3" }),
              _createElementVNode("line", {
                x1: "4",
                y1: "21",
                x2: "20",
                y2: "21"
              })
            ], -1)
          ]))], 2),
          _createElementVNode("button", {
            onClick: _cache[3] || (_cache[3] = ($event: any) => (_unref(editor).chain().focus().toggleStrike().run())),
            class: _normalizeClass([_unref(editor).isActive('strike') ? 'is-active' : '', "toolbar-btn"]),
            title: "Strikethrough"
          }, [...(_cache[19] || (_cache[19] = [
            _createElementVNode("svg", {
              class: "w-4 h-4",
              fill: "none",
              stroke: "currentColor",
              viewBox: "0 0 24 24"
            }, [
              _createElementVNode("line", {
                x1: "5",
                y1: "12",
                x2: "19",
                y2: "12"
              }),
              _createElementVNode("path", { d: "M16 6C16 6 14.5 4 12 4C9.5 4 7 5.5 7 8C7 10.5 9 11.5 12 12" }),
              _createElementVNode("path", { d: "M8 18C8 18 9.5 20 12 20C14.5 20 17 18.5 17 16C17 13.5 15 12.5 12 12" })
            ], -1)
          ]))], 2),
          _cache[32] || (_cache[32] = _createElementVNode("div", { class: "w-px h-5 bg-gray-200 mx-1" }, null, -1)),
          _createElementVNode("button", {
            onClick: _cache[4] || (_cache[4] = ($event: any) => (_unref(editor).chain().focus().toggleHeading({ level: 1 }).run())),
            class: _normalizeClass([_unref(editor).isActive('heading', { level: 1 }) ? 'is-active' : '', "toolbar-btn text-xs font-bold"]),
            title: "H1"
          }, "H1", 2),
          _createElementVNode("button", {
            onClick: _cache[5] || (_cache[5] = ($event: any) => (_unref(editor).chain().focus().toggleHeading({ level: 2 }).run())),
            class: _normalizeClass([_unref(editor).isActive('heading', { level: 2 }) ? 'is-active' : '', "toolbar-btn text-xs font-bold"]),
            title: "H2"
          }, "H2", 2),
          _createElementVNode("button", {
            onClick: _cache[6] || (_cache[6] = ($event: any) => (_unref(editor).chain().focus().toggleHeading({ level: 3 }).run())),
            class: _normalizeClass([_unref(editor).isActive('heading', { level: 3 }) ? 'is-active' : '', "toolbar-btn text-xs font-bold"]),
            title: "H3"
          }, "H3", 2),
          _cache[33] || (_cache[33] = _createElementVNode("div", { class: "w-px h-5 bg-gray-200 mx-1" }, null, -1)),
          _createElementVNode("button", {
            onClick: _cache[7] || (_cache[7] = ($event: any) => (_unref(editor).chain().focus().toggleBulletList().run())),
            class: _normalizeClass([_unref(editor).isActive('bulletList') ? 'is-active' : '', "toolbar-btn"]),
            title: "Bullet list"
          }, [...(_cache[20] || (_cache[20] = [
            _createStaticVNode("<svg class=\"w-4 h-4\" fill=\"none\" stroke=\"currentColor\" viewBox=\"0 0 24 24\"><line x1=\"9\" y1=\"6\" x2=\"20\" y2=\"6\"></line><line x1=\"9\" y1=\"12\" x2=\"20\" y2=\"12\"></line><line x1=\"9\" y1=\"18\" x2=\"20\" y2=\"18\"></line><circle cx=\"4\" cy=\"6\" r=\"1\" fill=\"currentColor\"></circle><circle cx=\"4\" cy=\"12\" r=\"1\" fill=\"currentColor\"></circle><circle cx=\"4\" cy=\"18\" r=\"1\" fill=\"currentColor\"></circle></svg>", 1)
          ]))], 2),
          _createElementVNode("button", {
            onClick: _cache[8] || (_cache[8] = ($event: any) => (_unref(editor).chain().focus().toggleOrderedList().run())),
            class: _normalizeClass([_unref(editor).isActive('orderedList') ? 'is-active' : '', "toolbar-btn"]),
            title: "Ordered list"
          }, [...(_cache[21] || (_cache[21] = [
            _createStaticVNode("<svg class=\"w-4 h-4\" fill=\"none\" stroke=\"currentColor\" viewBox=\"0 0 24 24\"><line x1=\"10\" y1=\"6\" x2=\"21\" y2=\"6\"></line><line x1=\"10\" y1=\"12\" x2=\"21\" y2=\"12\"></line><line x1=\"10\" y1=\"18\" x2=\"21\" y2=\"18\"></line><path d=\"M4 6h1v4\"></path><path d=\"M4 10H6\"></path><path d=\"M6 18H4l2-2a1.5 1.5 0 0 0-2.7-.9\"></path></svg>", 1)
          ]))], 2),
          _createElementVNode("button", {
            onClick: _cache[9] || (_cache[9] = ($event: any) => (_unref(editor).chain().focus().toggleBlockquote().run())),
            class: _normalizeClass([_unref(editor).isActive('blockquote') ? 'is-active' : '', "toolbar-btn"]),
            title: "Blockquote"
          }, [...(_cache[22] || (_cache[22] = [
            _createElementVNode("svg", {
              class: "w-4 h-4",
              fill: "currentColor",
              viewBox: "0 0 24 24"
            }, [
              _createElementVNode("path", { d: "M3 21c3 0 7-1 7-8V5c0-1.25-.756-2.017-2-2H4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2 1 0 1 0 1 1v1c0 1-1 2-2 2s-1 .008-1 1.031V20c0 1 0 1 1 1z" }),
              _createElementVNode("path", { d: "M15 21c3 0 7-1 7-8V5c0-1.25-.757-2.017-2-2h-4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2h.75c0 2.25.25 4-2.75 4v3c0 1 0 1 1 1z" })
            ], -1)
          ]))], 2),
          _cache[34] || (_cache[34] = _createElementVNode("div", { class: "w-px h-5 bg-gray-200 mx-1" }, null, -1)),
          _createElementVNode("button", {
            onClick: _cache[10] || (_cache[10] = ($event: any) => (_unref(editor).chain().focus().setTextAlign('left').run())),
            class: _normalizeClass([_unref(editor).isActive({ textAlign: 'left' }) ? 'is-active' : '', "toolbar-btn"]),
            title: "Align left"
          }, [...(_cache[23] || (_cache[23] = [
            _createElementVNode("svg", {
              class: "w-4 h-4",
              fill: "none",
              stroke: "currentColor",
              viewBox: "0 0 24 24"
            }, [
              _createElementVNode("line", {
                x1: "3",
                y1: "6",
                x2: "21",
                y2: "6"
              }),
              _createElementVNode("line", {
                x1: "3",
                y1: "12",
                x2: "15",
                y2: "12"
              }),
              _createElementVNode("line", {
                x1: "3",
                y1: "18",
                x2: "21",
                y2: "18"
              })
            ], -1)
          ]))], 2),
          _createElementVNode("button", {
            onClick: _cache[11] || (_cache[11] = ($event: any) => (_unref(editor).chain().focus().setTextAlign('center').run())),
            class: _normalizeClass([_unref(editor).isActive({ textAlign: 'center' }) ? 'is-active' : '', "toolbar-btn"]),
            title: "Align center"
          }, [...(_cache[24] || (_cache[24] = [
            _createElementVNode("svg", {
              class: "w-4 h-4",
              fill: "none",
              stroke: "currentColor",
              viewBox: "0 0 24 24"
            }, [
              _createElementVNode("line", {
                x1: "3",
                y1: "6",
                x2: "21",
                y2: "6"
              }),
              _createElementVNode("line", {
                x1: "6",
                y1: "12",
                x2: "18",
                y2: "12"
              }),
              _createElementVNode("line", {
                x1: "3",
                y1: "18",
                x2: "21",
                y2: "18"
              })
            ], -1)
          ]))], 2),
          _createElementVNode("button", {
            onClick: _cache[12] || (_cache[12] = ($event: any) => (_unref(editor).chain().focus().setTextAlign('right').run())),
            class: _normalizeClass([_unref(editor).isActive({ textAlign: 'right' }) ? 'is-active' : '', "toolbar-btn"]),
            title: "Align right"
          }, [...(_cache[25] || (_cache[25] = [
            _createElementVNode("svg", {
              class: "w-4 h-4",
              fill: "none",
              stroke: "currentColor",
              viewBox: "0 0 24 24"
            }, [
              _createElementVNode("line", {
                x1: "3",
                y1: "6",
                x2: "21",
                y2: "6"
              }),
              _createElementVNode("line", {
                x1: "9",
                y1: "12",
                x2: "21",
                y2: "12"
              }),
              _createElementVNode("line", {
                x1: "3",
                y1: "18",
                x2: "21",
                y2: "18"
              })
            ], -1)
          ]))], 2),
          _cache[35] || (_cache[35] = _createElementVNode("div", { class: "w-px h-5 bg-gray-200 mx-1" }, null, -1)),
          _createElementVNode("button", {
            onClick: setLink,
            class: _normalizeClass([_unref(editor).isActive('link') ? 'is-active' : '', "toolbar-btn"]),
            title: "Link"
          }, [...(_cache[26] || (_cache[26] = [
            _createElementVNode("svg", {
              class: "w-4 h-4",
              fill: "none",
              stroke: "currentColor",
              viewBox: "0 0 24 24"
            }, [
              _createElementVNode("path", { d: "M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" }),
              _createElementVNode("path", { d: "M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" })
            ], -1)
          ]))], 2),
          _createElementVNode("button", {
            onClick: addImage,
            class: "toolbar-btn",
            title: "Gambar"
          }, [...(_cache[27] || (_cache[27] = [
            _createElementVNode("svg", {
              class: "w-4 h-4",
              fill: "none",
              stroke: "currentColor",
              viewBox: "0 0 24 24"
            }, [
              _createElementVNode("rect", {
                x: "3",
                y: "3",
                width: "18",
                height: "18",
                rx: "2"
              }),
              _createElementVNode("circle", {
                cx: "8.5",
                cy: "8.5",
                r: "1.5"
              }),
              _createElementVNode("polyline", { points: "21 15 16 10 5 21" })
            ], -1)
          ]))]),
          _createElementVNode("button", {
            onClick: addYoutube,
            class: "toolbar-btn",
            title: "YouTube"
          }, [...(_cache[28] || (_cache[28] = [
            _createElementVNode("svg", {
              class: "w-4 h-4",
              fill: "currentColor",
              viewBox: "0 0 24 24"
            }, [
              _createElementVNode("path", { d: "M22.54 6.42a2.78 2.78 0 0 0-1.95-1.96C18.88 4 12 4 12 4s-6.88 0-8.59.46A2.78 2.78 0 0 0 1.46 6.42 29 29 0 0 0 1 12a29 29 0 0 0 .46 5.58 2.78 2.78 0 0 0 1.95 1.96C5.12 20 12 20 12 20s6.88 0 8.59-.46a2.78 2.78 0 0 0 1.95-1.96A29 29 0 0 0 23 12a29 29 0 0 0-.46-5.58z" }),
              _createElementVNode("polygon", {
                points: "9.75 15.02 15.5 12 9.75 8.98 9.75 15.02",
                fill: "white"
              })
            ], -1)
          ]))]),
          _cache[36] || (_cache[36] = _createElementVNode("div", { class: "w-px h-5 bg-gray-200 mx-1" }, null, -1)),
          _createElementVNode("button", {
            onClick: _cache[13] || (_cache[13] = ($event: any) => (_unref(editor).chain().focus().undo().run())),
            disabled: !_unref(editor).can().undo(),
            class: "toolbar-btn",
            title: "Undo"
          }, [...(_cache[29] || (_cache[29] = [
            _createElementVNode("svg", {
              class: "w-4 h-4",
              fill: "none",
              stroke: "currentColor",
              viewBox: "0 0 24 24"
            }, [
              _createElementVNode("polyline", { points: "9 14 4 9 9 4" }),
              _createElementVNode("path", { d: "M20 20v-7a4 4 0 0 0-4-4H4" })
            ], -1)
          ]))], 8, _hoisted_3),
          _createElementVNode("button", {
            onClick: _cache[14] || (_cache[14] = ($event: any) => (_unref(editor).chain().focus().redo().run())),
            disabled: !_unref(editor).can().redo(),
            class: "toolbar-btn",
            title: "Redo"
          }, [...(_cache[30] || (_cache[30] = [
            _createElementVNode("svg", {
              class: "w-4 h-4",
              fill: "none",
              stroke: "currentColor",
              viewBox: "0 0 24 24"
            }, [
              _createElementVNode("polyline", { points: "15 14 20 9 15 4" }),
              _createElementVNode("path", { d: "M4 20v-7a4 4 0 0 1 4-4h12" })
            ], -1)
          ]))], 8, _hoisted_4),
          _createElementVNode("button", {
            onClick: _cache[15] || (_cache[15] = ($event: any) => (_unref(editor).chain().focus().clearNodes().unsetAllMarks().run())),
            class: "toolbar-btn",
            title: "Clear formatting"
          }, [...(_cache[31] || (_cache[31] = [
            _createElementVNode("svg", {
              class: "w-4 h-4",
              fill: "none",
              stroke: "currentColor",
              viewBox: "0 0 24 24"
            }, [
              _createElementVNode("path", { d: "M12 20h9" }),
              _createElementVNode("path", { d: "M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" }),
              _createElementVNode("line", {
                x1: "2",
                y1: "2",
                x2: "22",
                y2: "22"
              })
            ], -1)
          ]))])
        ]))
      : _createCommentVNode("", true),
    _createVNode(_unref(EditorContent), {
      editor: _unref(editor),
      class: "tiptap-editor"
    }, null, 8, ["editor"]),
    (selectedImageEl.value)
      ? (_openBlock(), _createElementBlock("div", {
          key: 1,
          class: "image-toolbar fixed z-50 bg-white border border-gray-200 rounded-lg shadow-lg flex items-center gap-1 px-2 py-1.5",
          style: _normalizeStyle(imageToolbarStyle.value)
        }, [
          (_openBlock(), _createElementBlock(_Fragment, null, _renderList(imageSizes, (size) => {
            return _createElementVNode("button", {
              key: size.value,
              onClick: ($event: any) => (setImageWidth(size.value)),
              class: _normalizeClass([currentImageWidth.value === size.value ? 'bg-blue-100 text-blue-600' : 'text-gray-600 hover:bg-gray-100', "px-2 py-1 text-xs rounded font-medium transition-colors"])
            }, _toDisplayString(size.label), 11, _hoisted_5)
          }), 64))
        ], 4))
      : _createCommentVNode("", true)
  ]))
}
}

})